"use client";

import Image from "next/image";
import { useEffect, useState } from "react";
import { useSectionViewEvent } from "@/hooks/use-section-view-event";
import { piEvents, setSelectedExam as setTrackedExam } from "@/lib/analytics";
import { PI_SECTION } from "@/lib/analytics/schema";
import {
  examData,
  examNames,
  teacherPool,
  type ExamName,
} from "@/lib/exam-data";

/** Plain pill for a lecture/practice tag — Omni UI's `Badge` hardcodes near-black text regardless of theme. */
function Pill({ children }: { children: React.ReactNode }) {
  return (
    <span className="max-w-full rounded-full border border-pi-border-strong bg-pi-elevated px-3.5 py-1.5 text-sm leading-snug break-words text-pi-text">
      {children}
    </span>
  );
}

const DEFAULT_EXAM: ExamName = "JEE";

export function ExamExplorerSection() {
  const [selectedExam, setSelectedExam] = useState<ExamName>(DEFAULT_EXAM);
  const detail = examData[selectedExam];
  const teachers = detail.teachers
    .map(({ id, subject }) => ({ ...teacherPool[id], subject }))
    .filter(Boolean);

  useSectionViewEvent({
    sectionId: "educators",
    sectionName: PI_SECTION.EXAM_SELECTOR.name,
    sectionPosition: PI_SECTION.EXAM_SELECTOR.position,
  });

  useEffect(() => {
    setTrackedExam(DEFAULT_EXAM);
  }, []);

  const onExamSelect = (exam: ExamName, index: number) => {
    if (exam === selectedExam) return;

    piEvents.examSelected({
      selectedExam: exam,
      previousExam: selectedExam,
      selectionPosition: index + 1,
    });
    setTrackedExam(exam);
    setSelectedExam(exam);
  };

  return (
    <section
      id="educators"
      className="mx-auto max-w-[1200px] px-4 py-10 sm:px-6 lg:px-8 lg:py-16"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-4">
        <h2 className="m-0 text-[clamp(26px,4vw,34px)] font-semibold leading-tight text-pi-bright">
          What&apos;s in Pi for your exam
        </h2>
        <span className="text-sm text-pi-dim">Pick your track</span>
      </div>

      <div
        className="mt-[18px] flex gap-2.5 overflow-x-auto pb-1.5 [scrollbar-width:none] md:flex-wrap md:overflow-visible [&::-webkit-scrollbar]:hidden"
        role="tablist"
        aria-label="Exam tracks"
      >
        {examNames.map((exam, index) => (
          <button
            key={exam}
            type="button"
            role="tab"
            aria-selected={exam === selectedExam}
            onClick={() => onExamSelect(exam, index)}
            className={`max-w-full shrink-0 cursor-pointer rounded-full border px-[18px] py-2.5 font-sans text-sm font-semibold break-words whitespace-nowrap transition-all md:whitespace-normal ${
              exam === selectedExam
                ? "border-pi-accent bg-pi-accent-deep text-white"
                : "border-pi-border-muted bg-pi-panel text-pi-text"
            }`}
          >
            {exam}
          </button>
        ))}
      </div>

      <div className="mt-[18px] rounded-2xl border border-pi-border-strong bg-pi-panel p-[18px] sm:p-6">
        <div className="grid grid-cols-1 gap-[22px] md:grid-cols-2">
          <div className="min-w-0">
            <div className="mb-3 text-xs font-semibold tracking-wide text-pi-dim uppercase">
              Lectures in{" "}
              <span className="text-pi-accent-soft">{selectedExam}</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {detail.lectures.map((lecture) => (
                <Pill key={lecture}>{lecture}</Pill>
              ))}
            </div>
          </div>
          <div className="min-w-0">
            <div className="mb-3 text-xs font-semibold tracking-wide text-pi-dim uppercase">
              What you can practice
            </div>
            <div className="flex flex-wrap gap-2">
              {detail.practice.map((item) => (
                <Pill key={item}>{item}</Pill>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-[#31313A] pt-[18px]">
          <span className="text-xs font-semibold tracking-wide text-pi-dim uppercase">
            Teachers
          </span>
          {teachers.map((teacher) => (
            <span
              key={teacher.name}
              title={`${teacher.subject} · ${selectedExam}`}
              className="inline-flex max-w-full items-center gap-2 rounded-full border border-pi-border-strong bg-pi-elevated py-1.5 pr-3.5 pl-1.5"
            >
              <Image
                src={teacher.image}
                alt={teacher.name}
                width={30}
                height={30}
                className="block size-[30px] rounded-full bg-[#31313A] object-cover object-top"
              />
              <span className="min-w-0 text-sm font-medium break-words text-pi-bright">
                {teacher.name}
              </span>
            </span>
          ))}
          <span className="text-[13px] text-pi-dim">
            …and every PW educator&apos;s recorded library
          </span>
        </div>
      </div>
    </section>
  );
}
